import { CircleCheck, CircleDashed } from "lucide-react";
import { format, parseISO } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { StayPlanCharge } from "@/hooks/useStayPlanContracts";
import { formatDateBR } from "@/lib/date-utils";

export interface PlanPreviousCyclesProps {
  charges: StayPlanCharge[];
  /** Início do ciclo corrente; cobranças a partir dele não são "anteriores". */
  currentCycleStart: string | null;
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function PlanPreviousCycles({ charges, currentCycleStart }: PlanPreviousCyclesProps) {
  const { t, i18n } = useTranslation("patients");
  const locale = i18n.language.startsWith("pt") ? ptBR : enUS;

  const previous = charges
    .filter((charge) => !currentCycleStart || charge.periodStartsOn < currentCycleStart)
    .sort((a, b) => b.periodStartsOn.localeCompare(a.periodStartsOn));

  if (previous.length === 0) return null;

  return (
    <div className="mt-[18px] border-t border-border pt-3.5">
      <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-color-light">
        {t("planCard.previousCycles")}
      </p>
      <ul className="mt-2.5 space-y-[7px]">
        {previous.map((charge) => {
          const paid = charge.saleStatus === "PAGA";
          return (
            <li key={charge.id} className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-color-medium">
              {paid ? (
                <CircleCheck className="h-3.5 w-3.5 shrink-0 text-state-success" aria-hidden />
              ) : (
                <CircleDashed className="h-3.5 w-3.5 shrink-0 text-color-light" aria-hidden />
              )}
              <span className="capitalize">{format(parseISO(charge.periodStartsOn), "MMMM", { locale })}</span>
              <span>· {formatCurrency(charge.amount, i18n.language)}</span>
              {paid ? (
                <Link
                  to="/sales/history"
                  state={{ openSaleId: charge.saleId }}
                  aria-label={t("planCard.openPaidSale", { date: formatDateBR(charge.dueOn) })}
                  className="ms-auto text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {t("planCard.cyclePaidOn", { date: formatDateBR(charge.dueOn) })}
                </Link>
              ) : (
                <span className="ms-auto text-xs text-muted-foreground">
                  {t("planCard.cycleOpenDue", { date: formatDateBR(charge.dueOn) })}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
